import { NotificationsBellService } from '@app/services/notifications-bell/notifications-bell.service';
import { SettingsService } from '@app/services/settings.service';
import { UserService } from '@app/services/user/user.service';
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { IBellNotification, INotification } from '@app/services/notifications-bell/models/notifcations-bell-models';
import { UnsubscriberBase } from '@core/services/unsubscriber.base';
import { Organization } from '@app/interfaces/accounts_list';
import { expandAnimation, fadeAnimation, rotateAnimation } from '@modules/in-app-notifications/alerts/animations';

@Component({
	selector: 'app-alerts',
	templateUrl: './alerts.component.html',
	styleUrls: ['./alerts.component.scss'],
	animations: [expandAnimation, rotateAnimation, fadeAnimation],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertsComponent extends UnsubscriberBase implements OnInit, OnDestroy {
	@Input() allowedOrganizationsArray: Organization[];
	private expanded: string[] = [];
	private currentlyUpdatingNotifications: string[] = [];

	emailResendError: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
	emailWasSend: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

	public _notificationsToShow$: Observable<IBellNotification[]>;
	public _oldNotificationsToShow$: Observable<INotification[]>;

	public readonly isUpdating$: Observable<boolean> = this.notify.isUpdating$();

	isLoading = false;

	public showNotificationsForCurrentOrg = false;

	private _allNotificationsOfCurrentOrg = true;

	public set allNotificationsOfCurrentOrg(value: boolean) {
		this._allNotificationsOfCurrentOrg = value;
	}

	public get allNotificationsOfCurrentOrg(): boolean {
		return this._allNotificationsOfCurrentOrg;
	}

	@ViewChild('toggleRef')
	private dropdown: ElementRef;

	private _current_org_id: string;

	public set current_org_id(org_id: string) {
		this._current_org_id = org_id;
	}

	public get current_org_id(): string {
		return this._current_org_id;
	}

	constructor(
		private settingsService: SettingsService,
		private notify: NotificationsBellService,
		private userService: UserService,
	) {
		super();
	}

	public ngOnInit(): void {
		this._notificationsToShow$ = this.notify.getNotifications().pipe(
			tap((notifications) => {
				notifications?.forEach((notification: IBellNotification) => {
					if (!this.isCurrentOrg(notification.org_id)) {
						this.allNotificationsOfCurrentOrg = false;
					}
				});
			}),
		);

		this._oldNotificationsToShow$ = this.notify.getOldNotifications();

		this.current_org_id = this.userService.organizationId;
		this.userService.user$.pipe(filter(Boolean)).subscribe({
			next: () => {
				this.confirmEmailHandler();
				this.notify.fetchBellNotifications().pipe(takeUntil(this.unsubscribe)).subscribe();
			},
		});
	}

	public getExpandStatus(notification): string {
		return this.expanded.indexOf(notification.org_id + notification.date) >= 0 ? 'in' : 'out';
	}

	public determineNotificationStatus(notification): string {
		return this.currentlyUpdatingNotifications.indexOf(notification.org_id + notification.date) >= 0 ? 'in' : 'out';
	}

	public getNotificationUpdateStatus(notification): { [key: string]: boolean } {
		const isUpdating = this.currentlyUpdatingNotifications.indexOf(notification.org_id + notification.date) >= 0;
		return {
			'fa-window-close': !isUpdating,
			'fa-circle-notch': isUpdating,

			'fa-spin': isUpdating,
		};
	}

	public toggleHelpMenu(notification: any): void {
		if (this.expanded.indexOf(notification.org_id + notification.date) >= 0) {
			this.expanded = this.expanded.filter((item) => item !== notification.org_id + notification.date);
		} else {
			this.expanded = [...this.expanded, notification.org_id + notification.date];
		}
	}

	public updateLoadingNotifications(notification: IBellNotification): void {
		this.currentlyUpdatingNotifications = [
			...this.currentlyUpdatingNotifications,
			notification.org_id + notification.date,
		];
	}

	private filterByNotificationId(array: string[], id: string): string[] {
		return array.filter((notificationId) => {
			return notificationId !== id;
		});
	}

	public resetNotificationsList(notifications: IBellNotification[]): void {
		if (notifications.length) {
			const [{ org_id, date }] = notifications;
			this.currentlyUpdatingNotifications = this.filterByNotificationId(
				this.currentlyUpdatingNotifications,
				org_id + date,
			);
			this.expanded = this.filterByNotificationId(this.expanded, org_id + date);
		} else {
			this.currentlyUpdatingNotifications = [];
			this.expanded = [];
		}
	}

	public isCurrentOrg(org_id: string | number): boolean {
		return this.current_org_id === org_id;
	}

	public ngOnDestroy(): void {
		this.notify.setNotifications([]);
	}

	public dismiss(e: MouseEvent, specificNotification?: IBellNotification): void {
		e.stopPropagation();

		if (specificNotification) {
			this.updateLoadingNotifications(specificNotification);
		}

		const toDismiss = specificNotification ? [specificNotification] : [];

		this.notify
			.dismissNotification(toDismiss)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.resetNotificationsList(toDismiss);
			});
	}

	public confirmEmailHandler(): void {
		if (!this.userService.isUserEmailVerified) {
			this.notify.push({
				type: 'warning',
				text: 'Email not confirmed, please check your inbox.',
				dismissible: false,
				emailResend: true,
			});
		}
	}

	public onClosed(closedAlert: INotification): void {
		this.notify.delete(closedAlert);
	}

	public resendConfirmation(): void {
		this.isLoading = true;
		this.emailWasSend.next(false);
		this.emailResendError.next(false);
		this.settingsService.resendEmailConfirmation(this.userService.userEmail, this.userService.productLabel).subscribe({
			next: () => {
				this.isLoading = false;
				this.emailWasSend.next(true);
			},
			error: () => {
				this.isLoading = false;
				this.emailResendError.next(true);
			},
		});
	}
}
